import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { DietaryResponse, EventInfoWithConsentResponse, ParticipantRequest } from '@ent-regis/entregis-ts-angular';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { take } from 'rxjs/operators';
import { msg } from 'src/app/constants/msg';
import Swal from 'sweetalert2';
import { buildParticipantForm } from '../../forms/participant-form.factory';
import { CompanyParticipantModalComponent } from './company-participant-modal/company-participant-modal.component';

@Component({
  selector: 'app-company-participant-section',
  templateUrl: './company-participant-section.component.html',
  styleUrls: ['./company-participant-section.component.scss']
})
export class CompanyParticipantSectionComponent {

  @Input() participants!: FormArray;
  @Input() eventInfoWithConsent: EventInfoWithConsentResponse;
  @Input() noneDietaryId: number;
  @Input() dietaries: DietaryResponse[];
  @Input() registerCompanyId: number;  
  @Input() isCloseRegister: boolean = false;
  @Output() submitParticipants = new EventEmitter<ParticipantRequest[]>();

  maxContact = 1;
  maxParticipant = 5;
  
  get participantCount(): number {
    if (!this.participants || !this.participants.value) return 0;
    return this.participants.value.filter(p => p && (p.isParticipant === true || p.isAttendee === true)).length;
  }

  get contactCount(): number {
    if (!this.participants || !this.participants.value) return 0;
    return this.participants.value.filter(p => p && p.isContact === true).length;
  }

  addEditParticipantModalRef: BsModalRef;

  constructor(
    private modalService: BsModalService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.eventInfoWithConsent && this.eventInfoWithConsent) {
      this.maxParticipant = this.eventInfoWithConsent.participantLimit || 5;
    }
  }

  addParticipant(): void {
    const modalRef = this.modalService.show(CompanyParticipantModalComponent, {
      initialState: {
        eventInfoWithConsent: this.eventInfoWithConsent,
        noneDietaryId: this.noneDietaryId,
        dietaries: this.dietaries,
        contactCount: this.contactCount,
        participantCount: this.participantCount,
        maxParticipant: this.maxParticipant,
        registerCompanyId: this.registerCompanyId,
      },
      class: `modal-lg ${this.isCookieVisible ? 'modal-cookie-offset' : ''}`,
      backdrop: 'static',
      keyboard: false
    });

    modalRef.content.passData.pipe(take(1)).subscribe((p: ParticipantRequest) => {
      if (!p) return;       
      this.participants.push(buildParticipantForm(p, this.registerCompanyId, this.noneDietaryId));
    })
  }

  editParticipant(index: number): void {
    const currentParticipant = this.participants.at(index) as FormGroup;
    const modalRef = this.modalService.show(CompanyParticipantModalComponent, {
      initialState: {
        participant: currentParticipant,
        eventInfoWithConsent: this.eventInfoWithConsent,
        noneDietaryId: this.noneDietaryId,
        dietaries: this.dietaries,
        contactCount: this.contactCount,
        participantCount: this.participantCount,
        maxParticipant: this.maxParticipant,
        registerCompanyId: this.registerCompanyId,
      },
      class: `modal-lg ${this.isCookieVisible ? 'modal-cookie-offset' : ''}`,
      backdrop: 'static',
      keyboard: false
    });

   modalRef.content.passData.pipe(take(1)).subscribe((p: ParticipantRequest) => {
      if (!p) return;
      const formGroup = buildParticipantForm(p, this.registerCompanyId, this.noneDietaryId);
      this.participants.setControl(index, formGroup);
    });
  }

  removeParticipant(index: number): void {
    Swal.fire({
      heightAuto: false,
      title: msg.confirmRemove.title,
      text: msg.confirmRemove.text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: msg.confirmRemove.confirmButtonText,
      customClass: {
        confirmButton: 'swal-confirm-btn',
        cancelButton: 'swal-cancel-btn'
      },
      showClass: {
        popup: 'animate__animated animate__fadeIn animate__faster'
      },
      hideClass: {
        popup: 'animate__animated animate__fadeOut animate__faster'
      }
    }).then((result) => {
      if (result.value) {
        this.participants.removeAt(index);
      }
    });
  }
  
  hasReachedParticipantLimit(): boolean {
    return this.participantCount >= this.maxParticipant && this.contactCount >= this.maxContact;
  }

  get hasAtLeastOneContact(): boolean {
    return this.contactCount >= this.maxContact;
  }

  get canSaveParticipants(): boolean {
    return this.participants && this.participants.length > 0 && this.hasAtLeastOneContact && this.participants.valid;
  }

  get isCookieVisible(): boolean {
    const cookieName = 'SET_COOKIE_POLICY';
    const cookieVersion = '1.0.0';
    return !document.cookie.split(';').some(
      c => c.trim().startsWith(cookieName + '=') && c.includes(cookieVersion)
    );
  }

  onSubmit(): void {
    const body: ParticipantRequest[] = this.participants.controls.map(ctrl => {
      const v = ctrl.value;
      return {
        registerCompanyId: v.registerCompanyId,
        participantId: v.participantId,
        firstName: v.firstName,
        lastName: v.lastName,
        email: v.email,
        willReceiveEmail: v.willReceiveEmail,
        mobileNumber: v.mobileNumber,
        jobTitle: v.jobTitle,
        department: v.department,
        isParticipant: v.isParticipant,
        isAttendee: v.isAttendee,
        isContact: v.isContact,
        dietaryRequirementId: v.dietaryRequirementId,
        otherDietary: v.otherDietary,
        extNumber: v.extNumber,
      } as ParticipantRequest;
    });
    this.submitParticipants.emit(body);
  }

}
